import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { useAuth } from '../hooks/useAuth';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { LanguageSelector } from '../components/LanguageSelector';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // If token is present, show "set new password" form
  if (token) {
    return <SetNewPasswordForm token={token} />;
  }

  // Otherwise show "forgot password" form
  return <ForgotPasswordForm />;
}

function ForgotPasswordForm() {
  const { t } = useTranslation();
  const { forgotPasswordMutation } = useAuth();
  const [email, setEmail] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    forgotPasswordMutation.mutate({ email });
  };

  return (
    <div
      data-testid="reset-password-page"
      className="flex min-h-screen flex-col items-center justify-center p-12"
      style={{ backgroundColor: 'var(--background)' }}
    >
      <div className="absolute right-6 top-6">
        <LanguageSelector />
      </div>

      <div className="w-full max-w-[360px]">
        <h1
          className="mb-1"
          style={{
            fontSize: '1.375rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'var(--foreground-heading)',
          }}
        >
          {t('auth.resetTitle')}
        </h1>
        <p
          className="mb-7"
          style={{ fontSize: '0.8125rem', color: 'var(--foreground-muted)' }}
        >
          {t('auth.resetSubtitle')}
        </p>

        {forgotPasswordMutation.isError && (
          <ApiErrorMessage error={forgotPasswordMutation.error} />
        )}

        {forgotPasswordMutation.isSuccess ? (
          <div
            data-testid="forgot-password-success"
            className="flex items-start gap-2.5 rounded-[10px] p-3 px-4 mb-4"
            style={{
              backgroundColor: 'var(--success-light)',
              border: '1px solid rgba(183, 217, 69, 0.35)',
            }}
          >
            <span
              className="material-symbols-outlined shrink-0"
              style={{ fontSize: '16px', color: 'var(--success-dark)' }}
            >
              check_circle
            </span>
            <p className="text-xs font-semibold" style={{ color: 'var(--success-dark)' }}>
              {t('auth.resetSuccess')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="reset-password-form">
            <div className="mb-6 flex flex-col gap-[5px]">
              <label
                htmlFor="email"
                className="text-xs font-medium"
                style={{ color: 'var(--foreground-heading)' }}
              >
                {t('auth.email')}
              </label>
              <input
                id="email"
                data-testid="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="rounded-lg px-3 py-2 text-[0.8125rem]"
                style={{
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--background)',
                  color: 'var(--foreground)',
                }}
                placeholder={t('auth.email')}
              />
            </div>

            <button
              type="submit"
              data-testid="reset-submit"
              disabled={forgotPasswordMutation.isPending}
              className="w-full rounded-lg py-[0.6875rem] px-4 text-sm font-medium text-white transition-all duration-100 disabled:opacity-60"
              style={{
                backgroundColor: 'var(--primary)',
                border: '1px solid var(--primary)',
              }}
            >
              {forgotPasswordMutation.isPending
                ? t('common.loading')
                : t('auth.sendResetLink')}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/login"
            data-testid="back-to-login-link"
            className="text-xs font-medium"
            style={{ color: 'var(--secondary)' }}
          >
            {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
}

function SetNewPasswordForm({ token }: { token: string }) {
  const { t } = useTranslation();
  const { resetPasswordMutation } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [touched, setTouched] = useState({ password: false, confirm: false });

  const passwordError =
    touched.password && password.length > 0 && password.length < 8
      ? t('auth.passwordMinLength')
      : '';
  const confirmError =
    touched.confirm && confirmPassword.length > 0 && password !== confirmPassword
      ? t('auth.passwordsDoNotMatch')
      : '';
  const canSubmit = password.length >= 8 && password === confirmPassword;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    resetPasswordMutation.mutate({ token, password });
  };

  return (
    <div
      data-testid="set-new-password-page"
      className="flex min-h-screen flex-col items-center justify-center p-12"
      style={{ backgroundColor: 'var(--background)' }}
    >
      <div className="absolute right-6 top-6">
        <LanguageSelector />
      </div>

      <div className="w-full max-w-[360px]">
        <h1
          className="mb-1"
          style={{
            fontSize: '1.375rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'var(--foreground-heading)',
          }}
        >
          {t('auth.setNewPasswordTitle')}
        </h1>
        <p
          className="mb-7"
          style={{ fontSize: '0.8125rem', color: 'var(--foreground-muted)' }}
        >
          {t('auth.setNewPasswordSubtitle')}
        </p>

        {resetPasswordMutation.isError && (
          <ApiErrorMessage error={resetPasswordMutation.error} />
        )}

        <form onSubmit={handleSubmit} data-testid="set-new-password-form">
          <div className="mb-3.5 flex flex-col gap-[5px]">
            <label
              htmlFor="password"
              className="text-xs font-medium"
              style={{ color: 'var(--foreground-heading)' }}
            >
              {t('auth.newPassword')}
            </label>
            <input
              id="password"
              data-testid="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, password: true }))}
              required
              autoComplete="new-password"
              className="rounded-lg px-3 py-2 text-[0.8125rem]"
              style={{
                border: `1px solid ${passwordError ? 'var(--destructive)' : 'var(--border)'}`,
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
              }}
            />
            {passwordError && (
              <p data-testid="new-password-error" className="text-[0.6875rem]" style={{ color: 'var(--destructive)' }}>
                {passwordError}
              </p>
            )}
          </div>

          <div className="mb-6 flex flex-col gap-[5px]">
            <label
              htmlFor="confirmPassword"
              className="text-xs font-medium"
              style={{ color: 'var(--foreground-heading)' }}
            >
              {t('auth.confirmPassword')}
            </label>
            <input
              id="confirmPassword"
              data-testid="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
              required
              autoComplete="new-password"
              className="rounded-lg px-3 py-2 text-[0.8125rem]"
              style={{
                border: `1px solid ${confirmError ? 'var(--destructive)' : 'var(--border)'}`,
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
              }}
            />
            {confirmError && (
              <p data-testid="confirm-password-error" className="text-[0.6875rem]" style={{ color: 'var(--destructive)' }}>
                {confirmError}
              </p>
            )}
          </div>

          <button
            type="submit"
            data-testid="set-password-submit"
            disabled={!canSubmit || resetPasswordMutation.isPending}
            className="w-full rounded-lg py-[0.6875rem] px-4 text-sm font-medium text-white transition-all duration-100 disabled:opacity-60"
            style={{
              backgroundColor: 'var(--primary)',
              border: '1px solid var(--primary)',
            }}
          >
            {resetPasswordMutation.isPending
              ? t('common.loading')
              : t('auth.savePassword')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            data-testid="back-to-login-link"
            className="text-xs font-medium"
            style={{ color: 'var(--secondary)' }}
          >
            {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
}
